/**
 * Bühne und Zeichen: Arbeitsfläche, Schatten, Pfeile und Plaketten. Alle
 * Vorlagen nutzen dieselbe Bühne, damit sie wie aus einem Guss wirken.
 */
import { lerp, polar, rotatePoint } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { CANVAS, STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { path, polyline, smooth } from '../paths.ts';
import { circle, ellipse, group, label, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';

/** Höhe der Arbeitsfläche. Gegenstände stehen auf dieser Linie. */
export const GROUND_Y = 138;

/** Arbeitsfläche mit Vorderkante. */
export function counter(): SceneNode[] {
  return [
    rect(0, GROUND_Y, CANVAS.width, CANVAS.height - GROUND_Y, 0, styles.flat('tint')),
    line(0, GROUND_Y, CANVAS.width, GROUND_Y, { stroke: 'shade', strokeWidth: STROKE.detail }),
  ];
}

/**
 * Senkrechter Versatz, damit ein gedrehter Gegenstand mit seinem tiefsten
 * Punkt auf der Arbeitsfläche aufliegt statt zu schweben oder einzusinken.
 * Bei Drehung 0 ist der Versatz 0, wenn der Gegenstand schon aufliegt.
 * @returns Versatz in y und der Auflagepunkt nach Drehung und Versatz.
 */
export function restOnGround(
  contour: readonly Point[],
  degrees: number,
  origin: Point,
): { readonly dy: number; readonly contact: Point } {
  const lowest = (points: readonly Point[]): Point =>
    points.reduce((best, point) => (point.y > best.y ? point : best), points[0] ?? origin);
  const resting = lowest(contour).y;
  const turned = lowest(contour.map((point) => rotatePoint(point, origin, degrees)));
  const dy = resting - turned.y;
  return { dy, contact: { x: turned.x, y: turned.y + dy } };
}

/** Weicher Schatten unter einem Gegenstand. */
export function shadow(cx: number, rx: number, y = GROUND_Y + 1, ry = 3.5): SceneNode {
  return ellipse(cx, y, rx, ry, styles.flat('shadow'));
}

/** Gefüllte Pfeilspitze. */
function arrowHead(tip: Point, from: Point, size: number, tone: Tone): SceneNode {
  const angle = Math.atan2(tip.y - from.y, tip.x - from.x);
  const left = { x: tip.x - Math.cos(angle - 0.5) * size, y: tip.y - Math.sin(angle - 0.5) * size };
  const right = { x: tip.x - Math.cos(angle + 0.5) * size, y: tip.y - Math.sin(angle + 0.5) * size };
  return shape(polyline([left, tip, right], true), { fill: tone, stroke: tone, strokeWidth: STROKE.detail });
}

/** Pfeil entlang einer Punktfolge (geglättet). */
export function arrow(points: readonly Point[], tone: Tone = 'accent', head = 8): SceneNode {
  const tip = points[points.length - 1] as Point;
  const before = points[points.length - 2] as Point;
  // Die Linie endet kurz vor der Spitze, damit die Spitze sauber aussieht.
  const angle = Math.atan2(tip.y - before.y, tip.x - before.x);
  const end = { x: tip.x - Math.cos(angle) * head * 0.6, y: tip.y - Math.sin(angle) * head * 0.6 };
  return group([
    shape(smooth([...points.slice(0, -1), end], false), { stroke: tone, strokeWidth: STROKE.outline }),
    arrowHead(tip, before, head, tone),
  ]);
}

/** Bogenpfeil auf einer Ellipse, Winkel in Grad (0 rechts, 90 unten). */
export function arcArrow(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  fromDeg: number,
  toDeg: number,
  tone: Tone = 'accent',
): SceneNode {
  const points: Point[] = [];
  const steps = 12;
  for (let index = 0; index <= steps; index += 1) {
    const point = polar(0, 0, 1, lerp(fromDeg, toDeg, index / steps));
    points.push({ x: cx + point.x * rx, y: cy + point.y * ry });
  }
  return arrow(points, tone);
}

/** Doppelpfeil zwischen zwei Punkten, zum Beispiel für Hin- und Herbewegung. */
export function doubleArrow(from: Point, to: Point, tone: Tone = 'accent'): SceneNode {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const inset = 5;
  return group([
    line(
      from.x + Math.cos(angle) * inset,
      from.y + Math.sin(angle) * inset,
      to.x - Math.cos(angle) * inset,
      to.y - Math.sin(angle) * inset,
      { stroke: tone, strokeWidth: STROKE.outline },
    ),
    arrowHead(to, from, 8, tone),
    arrowHead(from, to, 8, tone),
  ]);
}

/** Geschätzte Breite eines Textes in der Bildschrift (fett). */
export function textWidth(text: string, size: number): number {
  let units = 0;
  for (const character of text) {
    if (/[il.,:;'!|/]/.test(character)) {
      units += 0.32;
    } else if (/[mwMW]/.test(character)) {
      units += 0.92;
    } else if (/[A-ZÄÖÜ0-9°]/.test(character)) {
      units += 0.68;
    } else if (character === ' ') {
      units += 0.28;
    } else {
      units += 0.58;
    }
  }
  return units * size;
}

/** Darstellung einer Plakette. */
export type BadgeTone = 'paper' | 'ink' | 'accent';

/**
 * Plakette mit kurzem Text, zum Beispiel „180 °C“ oder „3 Min“. Immer auf
 * deckendem Grund, damit der Text gut lesbar ist.
 */
export function badge(cx: number, cy: number, text: string, tone: BadgeTone = 'paper', size = 15): SceneNode {
  const width = textWidth(text, size) + size * 1.2;
  const height = size + 10;
  const textTone: Tone = tone === 'paper' ? 'ink' : 'paper';
  return group([
    rect(cx - width / 2, cy - height / 2, width, height, height / 2, {
      fill: tone,
      stroke: tone === 'accent' ? 'accent' : 'ink',
      strokeWidth: STROKE.detail,
    }),
    label(cx, cy + size * 0.36, text, size, { fill: textTone, weight: 800 }),
  ]);
}

/** Häkchen in einem Kreis – „fertig“. */
export function doneMark(cx: number, cy: number, scale = 1): SceneNode {
  const r = 11 * scale;
  return group([
    circle(cx, cy, r, { fill: 'ink', stroke: 'none' }),
    shape(
      path()
        .move(cx - r * 0.45, cy + r * 0.02)
        .line(cx - r * 0.1, cy + r * 0.38)
        .line(cx + r * 0.5, cy - r * 0.35)
        .toString(),
      { stroke: 'paper', strokeWidth: STROKE.outline },
    ),
  ]);
}

/** Kurze Zeitangabe: „20 Min“, „1 Std“, „1,5 Std“. */
export function durationLabel(minutes: number): string {
  if (minutes < 60) {
    return `${Math.round(minutes)} Min`;
  }
  const hours = Math.round((minutes / 60) * 2) / 2;
  return `${String(hours).replace('.', ',')} Std`;
}

/** Rechter Rand, an dem Plaketten oben rechts enden. */
export const BADGE_RIGHT = CANVAS.width - 8;

/**
 * Plakette mit Uhr und Zeitangabe. Der Zeiger dreht sich mit sweep
 * (0 bis 1 = eine Umdrehung) und zeigt so, dass Zeit vergeht.
 * @param x Mitte der Plakette, bei align = 'right' ihr rechter Rand.
 */
export function timerBadge(
  x: number,
  cy: number,
  text: string,
  sweep: number,
  align: 'center' | 'right' = 'right',
  size = 15,
): SceneNode {
  const textW = textWidth(text, size);
  const iconR = size * 0.5;
  const width = textW + iconR * 2 + size * 1.5;
  const height = size + 10;
  const left = align === 'right' ? x - width : x - width / 2;
  const iconX = left + size * 0.55 + iconR;
  const hand = polar(iconX, cy, iconR * 0.62, sweep * 360 - 90);
  return group([
    rect(left, cy - height / 2, width, height, height / 2, {
      fill: 'paper',
      stroke: 'ink',
      strokeWidth: STROKE.detail,
    }),
    circle(iconX, cy, iconR, { fill: 'paper', stroke: 'ink', strokeWidth: STROKE.detail }),
    // Der Akzentton bleibt der Hitze vorbehalten; der Zeiger ist in Tinte.
    line(iconX, cy - iconR * 0.6, iconX, cy - iconR * 0.85, { stroke: 'deep', strokeWidth: STROKE.fine }),
    line(iconX, cy, hand.x, hand.y, { stroke: 'ink', strokeWidth: STROKE.detail }),
    label(iconX + iconR + size * 0.35, cy + size * 0.36, text, size, { anchor: 'start', weight: 800 }),
  ]);
}
